import { createContext, ReactNode, useEffect, useReducer } from 'react';
// utils
import axios from '../utils/axios';
import { isValidToken, setSession } from '../utils/jwt';
import { storage, STORAGE_KEYS } from '../utils/storage';
// @types
import { ActionMap, AuthState, AuthUser, JWTContextType } from '../@types/auth';
import { API_ENDPOINTS } from 'src/api_endpoints';

// ----------------------------------------------------------------------

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [Types.Login]: {
    user: AuthUser;
  };
  [Types.Logout]: undefined;
  [Types.Register]: {
    user: AuthUser;
  };
};

export type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
      };
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };

    case 'REGISTER':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };

    default:
      return state;
  }
};

const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(JWTReducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        // Initialize storage and perform migration if needed
        await storage.initialize();

        // Preload cache for better performance
        await storage.preloadCache();

        // Add a small delay to ensure everything is ready
        await new Promise(resolve => setTimeout(resolve, 100));

        // Get access token from storage
        const accessToken = await storage.getItem<string>(STORAGE_KEYS.ACCESS_TOKEN);

        if (accessToken && isValidToken(accessToken)) {
          // Set the authorization header immediately
          axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

          await setSession(accessToken);

          try {
            const response = await axios.get(API_ENDPOINTS.users.myAccount);
            const user = response.data;

            dispatch({
              type: Types.Initial,
              payload: {
                isAuthenticated: true,
                user,
              },
            });
          } catch (userError) {
            console.error('Failed to get user data:', userError);

            // Token might be expired or invalid, clear it
            await storage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            delete axios.defaults.headers.common.Authorization;

            dispatch({
              type: Types.Initial,
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
          }
        } else {
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error('Authentication initialization failed:', err);
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (username: string, password: string) => {
    const response = await axios.post(API_ENDPOINTS.auth.login, {
      username,
      password,
    });
    const { accessToken, user } = response.data;

    await setSession(accessToken);

    dispatch({
      type: Types.Login,
      payload: {
        user,
      },
    });

    // Refresh page after successful login
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const register = async (email: string, password: string, firstName: string, lastName: string) => {
    const response = await axios.post(API_ENDPOINTS.auth.register, {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    await setSession(accessToken);

    dispatch({
      type: Types.Register,
      payload: {
        user,
      },
    });

    // Refresh page after successful registration
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const logout = async () => {
    await setSession(null);
    dispatch({ type: Types.Logout });

    // Refresh page after logout
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
